import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import api from 'state/axios';
import { format } from "date-fns-tz";
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box, Typography,
  CircularProgress, IconButton, Tooltip, 
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchGroupedRosterDetailsByCrewCode } from "state/api";
import { filterCrewMember } from "state/helpers";
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import PendingNotifList from "./PendingNotifList";


const CrewPendingNotifications = () => {
  useAuthCheck();
  const dispatch = useDispatch();
  const timeMode = useSelector((state) => state.timemode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);

  const [crewPendingNotifications, setCrewPendingNotifications] = useState([]);
    
  const crewRanksData = useSelector((state) => state.crewRanks);

  useEffect(() => {  
    const handleUnfinalizedCrewRoutes = async () => {
      if (!startDate || !endDate) return null; 
      
      try {
        setError(null);
        setLoading(true);
        
        const url = '/crewpendingnotifications';
        const payload = { startDate, endDate };          
        const res = await api.post(url, payload);
        const updatedData = res.data?.filter(row => row.isActive)

        setCrewPendingNotifications(updatedData || []);
      } catch (error) {
        setError(error)
        toast.error(error?.message || "Failed to fetch crew pending notifications!");
      } finally {
        setLoading(false);
      }
    };
    
    handleUnfinalizedCrewRoutes();
  }, [dispatch, timeMode, startDate, endDate]); 
        
  const filteredData = useMemo(() => {
    const data = crewPendingNotifications;

    if (!Array.isArray(data)) return [];
    if (!filters) return data;
    return data.filter(member => filterCrewMember(member, filters));
  }, [crewPendingNotifications, filters]);
  
  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleViewClick = useCallback(async (param) => {
    try {
      const crewCode = param.crewCode;
      const crewName = param.crewName;
      setSelectedCrewCode(crewCode);
      setSelectedCrewName(crewName);
      const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
                
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
    } catch (error) {
      setOpenRosterDetailsDialog(false);
    } 
  }, [startDate, endDate]);
    
  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      flex: 1,
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },    
    {
      field: "crewCode",
      headerName: "Code",
      flex: 1,      
    },
    {
      field: "crewName",
      headerName: "CrewName",
      flex: 4,
      renderCell: (params) => {
        const gender = params.row.gender; 
        return (
          <Box
            sx={{
              color: gender === 'FM' 
                ? theme.palette.secondary.main 
                : theme.palette.primary.dark,
            }}
          >
            {params.value}
          </Box>
        );
      },
    }, 
    {
      field: "post",
      headerName: "Post",
      flex: 1,
    },
    {
      field: "base",
      headerName: "Base",
      flex: 1,
    },
    {
      field: 'notifications',
      headerName: 'Notifications',
      flex: 10,    
      renderCell: (params) => {
        const sortedNotifications = [...params.row.notifications].sort(
          (a, b) => new Date(a.logForDate) - new Date(b.logForDate)
        );

        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {sortedNotifications?.map((notif, index) => (
              <Box key={index} display="flex" flexDirection="column">
                <Typography variant="caption" >
                  {format(notif.logForDate, 'dd/MM/yy')} {notif.logMessage} by {notif.logByUser} at {notif.logOnDate} {notif.logOnTime}                  
                </Typography>                
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      flex: 1,
    },
  ], [theme]);

  const actionColumn = useMemo(() => [{
    field: "action",
    headerName: "Action",
    filterable: false,      
    sortable: false,
    width: 80,       
    renderCell: (params) => (
      <Box 
        height="100%" 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        gap={2}
      >
        <Tooltip title="View">
          <IconButton
            aria-label="View"
            sx={{
              color: theme.palette.primary.dark,
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.green.deep,
              }
            }}
            onClick={() => {              
              handleViewClick(params.row)}
            }
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>          
      </Box>
    ),
  }], [theme, handleViewClick]); 

  const hiddenColumns = useMemo(() => {
    return {
      _id: false,
      no: false,
      isActive: false,
    };
  }, []); 
  
  const nonMobileHiddenColumns = useMemo(() => { 
    return {
      _id: false,
      isActive: false,
    }
  }, []);
  
  const baseOptions = [...new Set(crewPendingNotifications?.map(member => member.base))].sort();
  
  const postOptions = useMemo(() => (
    [...new Set(crewPendingNotifications.map(m => m.post))]
      .map(post => crewRanksData.find(r => r.rankCode === post))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode)
  ), [crewPendingNotifications, crewRanksData]);

  const trainingQualOptions = useMemo(() => (
    [...new Set(
      crewPendingNotifications
        .flatMap(member => member.trainingQuals || [])
        .filter(Boolean)
    )].sort()
  ), [crewPendingNotifications]);

  const columnsToShow = [...columns, ...actionColumn];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Crew Pending Notifications" 
          subtitle={`${loading ? 'Loading...' : 'List of Notifications'}`}
        />       
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}> 
            <Tooltip title="Filter crew members" arrow>
              <StyledButton onClick={handleFilterOpen}>
                <FilterAltIcon />
              </StyledButton>
            </Tooltip>
          </Box>
        )}           
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
          >                            
            {isMobile ? (
              <PendingNotifList
                rows={filteredData}
                onView={handleViewClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredData}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>          
        </Box>
      )}

      <ScrollToTop />
      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          currentFilters={filters}
        />
      }

      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default CrewPendingNotifications;
